/**
 * LitLab API layer — kept self-contained so this section can be forked out
 * as an independent React app later.
 */
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const LITLAB_API = `${BASE}/api/litlab`;

const http = axios.create({ baseURL: `${BASE}/api`, timeout: 30000 });

// Attach Bearer token from the shared WebsiteTech auth store so admin
// endpoints (article CMS + Iris FAQ CMS) authenticate correctly.
http.interceptors.request.use((config) => {
  const token = typeof window !== "undefined" && localStorage.getItem("wt_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const litlabSite = () => http.get("/litlab/site").then((r) => r.data);
export const litlabIndustries = () => http.get("/litlab/industries").then((r) => r.data);
export const litlabIndustry = (slug) => http.get(`/litlab/industries/${slug}`).then((r) => r.data);
export const litlabServices = () => http.get("/litlab/services").then((r) => r.data);
export const litlabArticles = (kind) =>
  http.get("/litlab/articles", { params: kind ? { kind } : {} })
    .then((r) => r.data);
export const litlabArticlePut = (kind, slug, payload) =>
  http.put(`/litlab/articles/${kind}/${slug}`, payload).then((r) => r.data);
export const litlabArticleHistory = (kind, slug) =>
  http.get(`/litlab/articles/${kind}/${slug}/history`).then((r) => r.data);
export const litlabArticleRevert = (kind, slug, revId) =>
  http.post(`/litlab/articles/${kind}/${slug}/revert/${revId}`).then((r) => r.data);
export const litlabQnaList = (limit = 300) =>
  http.get("/litlab/agent/qna", { params: { limit } }).then((r) => r.data);
export const litlabQnaPut = (qid, payload) =>
  http.put(`/litlab/agent/qna/${encodeURIComponent(qid)}`, payload)
    .then((r) => r.data);
export const litlabQnaDelete = (qid) =>
  http.delete(`/litlab/agent/qna/${encodeURIComponent(qid)}`)
    .then((r) => r.data);
export const litlabFormOptions = () => http.get("/litlab/form-options").then((r) => r.data);
export const litlabSubmitQuote = (p) => http.post("/litlab/quote", p).then((r) => r.data);

// Contact-us form
export const litlabContactCaptcha = () => http.get("/litlab/contact/captcha").then((r) => r.data);
export const litlabSubmitContact = (p) => http.post("/litlab/contact", p).then((r) => r.data);

// Case studies
export const litlabCaseStudies = () => http.get("/litlab/case-studies").then((r) => r.data);
export const litlabCaseStudy = (slug) => http.get(`/litlab/case-studies/${slug}`).then((r) => r.data);
export const litlabCaseStudyGenerate = (slug) => http.post(`/litlab/case-studies/${slug}/image`).then((r) => r.data);
export const litlabCaseStudyImageUrl = (slug) => `${LITLAB_API}/case-studies/${slug}/image`;

// Deep-content pages scraped/authored per service topic
export const litlabPagesList = () => http.get("/litlab/pages").then((r) => r.data);
export const litlabPageGet = (slug) => http.get(`/litlab/pages/${slug}`).then((r) => r.data);
export const litlabPagePut = (slug, payload) =>
  http.put(`/litlab/pages/${slug}`, payload).then((r) => r.data);
export const litlabPageCreate = (payload) =>
  http.post("/litlab/pages", payload).then((r) => r.data);
export const litlabPageDelete = (slug) =>
  http.delete(`/litlab/pages/${slug}`).then((r) => r.data);
export const litlabPageSync = (payload) =>
  http.post("/litlab/admin/pages/sync", payload).then((r) => r.data);
export const litlabPageSyncAll = (overwrite = false) =>
  http.post(`/litlab/admin/pages/sync-all?overwrite=${overwrite}`).then((r) => r.data);

// i18n + About tabs + Uploads
export const litlabAboutTabsGet = () => http.get("/litlab/about/tabs").then((r) => r.data);
export const litlabAboutTabsPut = (tabs) =>
  http.put("/litlab/about/tabs", { tabs }).then((r) => r.data);
export const litlabTranslateLanguages = () =>
  http.get("/litlab/translate/languages").then((r) => r.data);
export const litlabUploadImage = (file) => {
  const form = new FormData(); form.append("file", file);
  return http.post("/litlab/uploads", form).then((r) => r.data);
};
